import { useCallback, useRef } from 'react';
import { NodeChange } from '@xyflow/react';
import {
  CanvasEdgeConnectionType,
  CanvasElementType,
  CanvasEventType,
  CanvasMoveNodeEvent,
  CanvasMoveGroupEvent,
  CanvasMoveStandaloneNodeEvent,
  CanvasMoveSectionEvent,
  CanvasMoveStickyNoteEvent,
  CanvasMoveTextNodeEvent,
} from '../../canvas-types';
import { snapPositionToGrid } from '../../helpers/gridPositioningUtils';
import { SweepCanvasRfNode } from '../../canvas-types/nodeTypesData';
import { updateSectionIsDropTargetProperty } from '../../sections/updateSectionIsDropTargetProperty';
import { createObjectOperations } from '../objectOperations';
import { RFEdgeFloatingEdge } from '../../edges';
import { useOnRegularNodePositionChange } from './useOnRegularNodePositionChange';
import { useOnGroupNodePositionChange } from './useOnGroupNodePositionChange';
import { useOnStandaloneNodePositionChange } from './useOnStandaloneNodePositionChange';
import { useOnStickyNodePositionChange } from './onStickyNodePositionChange';
import { MovingNodesOriginalPositionRef, NodeTransformations } from '../useOnNodeChange';
import { Node } from '@xyflow/react';
import { useGetIntersectionSection } from '../../sections/useGetIntersectionSection';
import { useSweepCanvasState } from '../../internal-context/CanvasStateContext';
import { useSweepCanvasPropsCtx } from '../../internal-context/SweepCanvasPropsCtx';
import { useOnTextNodePositionChange } from './useOnTextNodePositionChange';

const updateEdgesForDraggingNodes = ({
  nodeChanges,
  nodeTransformations,
  edges,
  edgeOperations,
}: {
  nodeChanges: NodeChange<SweepCanvasRfNode>[];
  nodeTransformations: NodeTransformations;
  edges: RFEdgeFloatingEdge[];
  edgeOperations: ReturnType<typeof createObjectOperations<RFEdgeFloatingEdge>>;
}) => {
  const allDraggingNodesIds = [
    ...nodeChanges.filter((nodeChange) => nodeChange.type === 'position'),
    ...nodeTransformations.extraNodePositionChanges,
  ]
    .filter((nodeChange) => nodeChange.dragging)
    .map((nodeChange) => nodeChange.id);

  edges.forEach((edge) => {
    if (!edge.data) return;

    const connectedNodesDragging =
      allDraggingNodesIds.includes(edge.source) || allDraggingNodesIds.includes(edge.target);

    // Only update if the dragging state has changed
    if (edge.data.connectedNodesDragging !== connectedNodesDragging) {
      edgeOperations.setObject(edge.id, (edge) => ({
        ...edge,
        data: {
          ...edge.data,
          connectedNodesDragging,
          ...(connectedNodesDragging && { connectionType: CanvasEdgeConnectionType.Bezier }),
        } as RFEdgeFloatingEdge['data'],
      }));
    }
  });
};

export const useOnNodePositionChanges = () => {
  const movingNodesRef = useRef<MovingNodesOriginalPositionRef>({});
  const { onRegularNodePositionChange } = useOnRegularNodePositionChange(movingNodesRef);
  const { onGroupNodePositionChange } = useOnGroupNodePositionChange(movingNodesRef);
  const { onStandaloneNodePositionChange } = useOnStandaloneNodePositionChange();
  const { onStickyNodePositionChange } = useOnStickyNodePositionChange();
  const { onTextNodePositionChange } = useOnTextNodePositionChange();
  const { getIntersectionSection } = useGetIntersectionSection();
  const { onSweepNodesChange } = useSweepCanvasPropsCtx();
  const { setCanvasEdges: setEdges } = useSweepCanvasState();

  // Records the initial position of a node when it starts moving
  const maybeRecordInitialPosition = useCallback((node?: Node) => {
    if (node && !movingNodesRef.current[node.id]) {
      movingNodesRef.current[node.id] = { originalPosition: node.position };
    }
  }, []);

  const handlePositionChanges = useCallback(
    ({
      positionChanges,
      nodeOperations,
      hasDimensionChange,
    }: {
      positionChanges: NodeChange<SweepCanvasRfNode>[];
      nodeOperations: ReturnType<typeof createObjectOperations<SweepCanvasRfNode>>;
      hasDimensionChange: boolean;
    }) => {
      const nodeTransformations: NodeTransformations = { extraNodePositionChanges: [] }; // Transformed by reference

      const moveEvents: Array<
        | CanvasMoveNodeEvent
        | CanvasMoveGroupEvent
        | CanvasMoveStandaloneNodeEvent
        | CanvasMoveSectionEvent
        | CanvasMoveStickyNoteEvent
        | CanvasMoveTextNodeEvent
      > = [];
      const { getObject: getNode } = nodeOperations;

      positionChanges.forEach((nodeChange) => {
        if (nodeChange.type === 'position') {
          const node = getNode(nodeChange.id);

          maybeRecordInitialPosition(node);
          if (node && nodeChange.position) {
            const intersectionSectionNode = getIntersectionSection(node);
            if (
              !nodeChange.dragging &&
              node.type !== CanvasElementType.SECTION &&
              node.type !== CanvasElementType.STICKY_NOTE &&
              node.type !== CanvasElementType.TEXT_NODE
            ) {
              nodeChange.position = snapPositionToGrid(nodeChange.position, {
                withPaddingByNodeType: node.type,
              });
            }

            updateSectionIsDropTargetProperty({
              intersectionSectionNode,
              dragging: nodeChange.dragging,
              nodeOperations,
              nodeType: node.type,
            });

            switch (node.type) {
              case CanvasElementType.REGULAR: {
                const changes = onRegularNodePositionChange({
                  nodeChange,
                  node: node as any,
                  nodeTransformations,
                  nodeOperations,
                });

                if (changes?.length) {
                  changes.forEach((change) => {
                    if (
                      change.type === CanvasEventType.MoveNode ||
                      change.type === CanvasEventType.MoveGroup ||
                      change.type === CanvasEventType.MoveStandaloneNode
                    ) {
                      moveEvents.push(change);
                    }
                  });
                }
                break;
              }
              case CanvasElementType.GROUP_LABEL:
              case CanvasElementType.GROUP: {
                const groupChanges = onGroupNodePositionChange({
                  nodeChange,
                  node: node as any,
                  nodeTransformations,
                  nodeOperations,
                  intersectionSectionNode: intersectionSectionNode as any,
                });
                if (groupChanges) {
                  if (groupChanges.type === CanvasEventType.MoveGroup) {
                    moveEvents.push(groupChanges);
                  }
                }
                break;
              }
              case CanvasElementType.STANDALONE_NODE: {
                const changes = onStandaloneNodePositionChange({
                  nodeChange,
                  node: node as any,
                  nodeOperations,
                  intersectionSectionNode: intersectionSectionNode as any,
                });
                if (changes) {
                  if (changes.type === CanvasEventType.MoveStandaloneNode) {
                    moveEvents.push(changes);
                  }
                }
                break;
              }
              case CanvasElementType.SECTION: {
                if (!nodeChange.dragging) {
                  moveEvents.push({
                    type: CanvasEventType.MoveSection,
                    id: nodeChange.id,
                    xyPosition: nodeChange.position,
                  });
                }
                break;
              }
              case CanvasElementType.STICKY_NOTE: {
                const changes = onStickyNodePositionChange({
                  nodeChange,
                  node: node as any,
                  nodeOperations,
                  intersectionSectionNode: intersectionSectionNode as any,
                  isResizing: hasDimensionChange,
                });
                if (changes) {
                  if (changes.type === CanvasEventType.MoveStickyNote) {
                    moveEvents.push(changes);
                  }
                }
                break;
              }
              case CanvasElementType.TEXT_NODE: {
                const changes = onTextNodePositionChange({
                  nodeChange,
                  node: node as any,
                  nodeOperations,
                  intersectionSectionNode: intersectionSectionNode as any,
                  isResizing: hasDimensionChange,
                });
                if (changes) {
                  if (changes.type === CanvasEventType.MoveTextNode) {
                    moveEvents.push(changes);
                  }
                }
                break;
              }
              default:
                break;
            }
          }
        }
      });

      if (moveEvents.length > 0) {
        if (moveEvents.length > 1) {
          // Bundle all move events into a single CanvasMoveMultipleNodesEvent
          onSweepNodesChange?.({
            type: CanvasEventType.MoveMultipleNodes,
            moveEvents,
          });
        } else {
          moveEvents.forEach((moveEvent) => {
            onSweepNodesChange?.(moveEvent);
          });
        }
      }
      
      setEdges((currentEdges) => {
        const currentEdgeOperations = createObjectOperations(currentEdges);
        updateEdgesForDraggingNodes({
          nodeChanges: positionChanges,
          nodeTransformations,
          edges: currentEdges,
          edgeOperations: currentEdgeOperations,
        });
        return currentEdgeOperations.getObjects();
      });
      return nodeTransformations.extraNodePositionChanges;
    },
    [
      setEdges,
      maybeRecordInitialPosition,
      getIntersectionSection,
      onRegularNodePositionChange,
      onGroupNodePositionChange,
      onStandaloneNodePositionChange,
      onStickyNodePositionChange,
      onTextNodePositionChange,
      onSweepNodesChange,
    ],
  );

  return { handlePositionChanges };
};
